/**
 * Acciones rápidas «Hoy» y «Ahora» en los selectores de fecha y hora
 * -----------------------------------------------------------------------------
 * MDB no trae ningún atajo para la fecha o la hora actual: hay que navegar hasta
 * el día de hoy o mover las agujas del reloj a mano. Aquí se añade un botón en
 * el footer de cada picker.
 *
 * Por qué un MutationObserver y no un evento: el datepicker emite
 * `open.mdb.datepicker`, pero el timepicker no emite nada al abrirse. Observar
 * la inserción del modal en el body cubre los dos por igual y no depende de que
 * MDB mantenga sus eventos.
 *
 * El valor se escribe en el input y se cierra el modal:
 *   - Datepicker: `close()` es público, y `formatDate()` respeta el formato
 *     configurado (`dd/mm/yyyy`, `yyyy-mm-dd`…).
 *   - Timepicker: no expone `close()`, así que se reenvía el clic a su botón
 *     cancelar, que solo desmonta el modal sin restaurar el valor anterior.
 *
 * Opt-out: `data-mdb-no-quick-action` en el elemento del picker.
 */

import { quickActionsES } from './locale-es.js';

const CLASS_QUICK = 'company-quick-action';

// El callback del MutationObserver no recibe contexto, así que los componentes
// se guardan en el módulo al arrancar.
let Datepicker = null;
let Timepicker = null;
let Datetimepicker = null;

function dispatchInput(input) {
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Busca la instancia que está abierta ahora mismo entre todos los pickers.
 */
function findOpen(selector, Component, isOpen) {
  const elements = document.querySelectorAll(selector);

  for (let i = 0; i < elements.length; i += 1) {
    const instance = Component && Component.getInstance(elements[i]);
    if (instance && isOpen(instance)) return { element: elements[i], instance };
  }

  return null;
}

/**
 * Igual que `findOpen`, pero mirando dentro de los datetimepicker: sus pickers
 * de fecha y hora son instancias internas (`_datepicker` / `_timepicker`) que no
 * llevan atributo propio en el DOM, así que no aparecen en la búsqueda directa.
 *
 * Se devuelve la instancia INTERNA, que es la que hay que manipular: el
 * datetimepicker lee el valor de su datepicker al cerrarse
 * (`EventHandler.one(..., close.mdb.datepicker)`) y recompone el campo final.
 */
function findOpenNested(key, isOpen) {
  const elements = document.querySelectorAll('[data-mdb-datetimepicker-init]');

  for (let i = 0; i < elements.length; i += 1) {
    const outer = Datetimepicker && Datetimepicker.getInstance(elements[i]);
    const inner = outer && outer[key];
    if (inner && isOpen(inner)) return { element: elements[i], instance: inner };
  }

  return null;
}

function makeButton(label, className) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = `${className} ${CLASS_QUICK}`;
  button.textContent = label;
  return button;
}

// -----------------------------------------------------------------------------
// Datepicker — «Hoy»
// -----------------------------------------------------------------------------

function todayWithinRange(options) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const { min, max, disablePast, disableFuture } = options || {};
  if (min && today < new Date(min)) return false;
  if (max && today > new Date(max)) return false;
  if (disableFuture && today > new Date()) return false;
  if (disablePast && today < new Date()) return false;

  return true;
}

function injectToday(container) {
  const footer = container.querySelector('.datepicker-footer');
  if (!footer || footer.querySelector(`.${CLASS_QUICK}`)) return;

  const open =
    findOpen('[data-mdb-datepicker-init]', Datepicker, (i) => i._isOpen) ||
    findOpenNested('_datepicker', (i) => i._isOpen);
  if (!open) return;
  if (open.element.hasAttribute('data-mdb-no-quick-action')) return;

  const button = makeButton(quickActionsES.today, 'datepicker-footer-btn');

  if (!todayWithinRange(open.instance._options)) {
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
  }

  button.addEventListener('click', () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const input = open.instance._input || open.element.querySelector('input');
    input.value = open.instance.formatDate(today);
    input.classList.add('active');

    open.instance.close();
    dispatchInput(input);
  });

  // Primero en el footer: las acciones destructivas (Limpiar/Cancelar) y la de
  // confirmación quedan donde el usuario ya las espera, a la derecha.
  footer.insertBefore(button, footer.firstChild);
}

// -----------------------------------------------------------------------------
// Timepicker — «Ahora»
// -----------------------------------------------------------------------------

function formatNow(options) {
  const now = new Date();
  const hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, '0');

  if (options && options.format24) {
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }

  const hour12 = hours % 12 || 12;
  return `${String(hour12).padStart(2, '0')}:${minutes} ${hours < 12 ? 'AM' : 'PM'}`;
}

function injectNow(modal) {
  const footer = modal.querySelector('.timepicker-footer, .timepicker-clock-wrapper + div');
  const buttons = modal.querySelector('.timepicker-submit')
    ? modal.querySelector('.timepicker-submit').parentNode
    : footer;
  if (!buttons || buttons.querySelector(`.${CLASS_QUICK}`)) return;

  const open =
    findOpen('[data-mdb-timepicker-init]', Timepicker, (i) => !!i._modal) ||
    findOpenNested('_timepicker', (i) => !!i._modal);
  if (!open) return;
  if (open.element.hasAttribute('data-mdb-no-quick-action')) return;

  const button = makeButton(quickActionsES.now, 'timepicker-button');

  button.addEventListener('click', () => {
    const input = open.instance.input || open.element.querySelector('input');
    input.value = formatNow(open.instance._options);
    input.classList.add('active');

    // Dentro de un datetimepicker hay que avisar antes de cerrar: MDB recoge la
    // hora escuchando `valueChanged.mdb.timepicker` sobre el elemento del reloj
    // interno y recompone el campo final. Sin esto, cerrar por «Cancelar» le
    // hace descartar también la fecha ya elegida.
    open.instance._element.dispatchEvent(
      new CustomEvent('valueChanged.mdb.timepicker', { bubbles: true })
    );

    const cancel = modal.querySelector('.timepicker-cancel');
    if (cancel) cancel.click();

    dispatchInput(input);
  });

  buttons.insertBefore(button, buttons.firstChild);
}

// -----------------------------------------------------------------------------

let observer = null;

/**
 * Empieza a vigilar la apertura de pickers para añadirles su acción rápida.
 *
 * @param {Object} components Componentes de MDB ({ Datepicker, Timepicker }).
 */
export function setupPickerActions(components = {}) {
  if (typeof document === 'undefined' || observer) return;

  Datepicker = components.Datepicker;
  Timepicker = components.Timepicker;
  Datetimepicker = components.Datetimepicker;

  observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!node || node.nodeType !== 1) return;

        if (node.matches('.datepicker-modal-container, .datepicker-dropdown-container')) {
          injectToday(node);
        } else if (node.matches('.timepicker-wrapper, .timepicker-modal')) {
          injectNow(node);
        }
      });
    });
  });

  observer.observe(document.body, { childList: true });
}

export default setupPickerActions;
